import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AdminTenant } from '@/features/tenants/useTenants';

import { classifyPinResetError, useCommandPinReset } from './useCommandPinReset';
import { useTenantUsers, type TenantUser } from './useTenantUsers';

/**
 * SLICE 14c/14d — CLEAR a tenant user's command PIN. Follows `ResetPasswordDialog`
 * chrome and keeps its three stages, but for a DIFFERENT and more easily-misread
 * action.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 THE CONFIRMATION SAYS "CLEARS", NEVER "SHOWS" AND NEVER "RESETS TO".
 * ─────────────────────────────────────────────────────────────────────────────
 * An operator reaching for this in a hurry might think it reveals a forgotten PIN
 * or sets a known one. It does neither: it empties the PIN, nobody (Raad included)
 * can see the old one, and the user must choose a new one before any engine
 * command will work again. Clearing is the SAFE direction — with no PIN every
 * command refuses, so a support action taken in haste cannot make a vehicle more
 * commandable than it was. The words on the confirm screen carry that, because
 * the route's shape cannot.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 SLICE 14d — THE OPERATOR PICKS A PERSON, NOT A UUID.
 * ─────────────────────────────────────────────────────────────────────────────
 * Mahad: "i click on reset command PIN, it asks me user ID which is long and i
 * dont know, simplify this dont complicate." So the typed uuid is gone: the
 * dialog fetches the tenant's users (`GET /api/admin/tenants/:tenantId/users`,
 * super-admin) and offers a picker. Each option shows the name (or phone when
 * name is null), the phone, the role, and whether there is a PIN to clear — so
 * the operator can SEE there is nothing to clear before clearing it. A DISABLED
 * account is listed and labelled, never hidden: silently omitting a person the
 * operator knows exists is how they conclude provisioning is broken. The confirm
 * names the PERSON, never an id.
 */

export function CommandPinResetDialog({
  tenant,
  onClose,
}: {
  tenant: AdminTenant;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  // Super-admin only reaches this dialog (it is opened solely from the
  // super-admin action in `Tenants`), so the query is enabled unconditionally;
  // the route is `requireSuperAdmin` and a lesser principal never mounts this.
  const usersQ = useTenantUsers(tenant.id, true);
  const [userId, setUserId] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const reset = useCommandPinReset();

  const users = Array.isArray(usersQ.data?.users) ? usersQ.data.users : [];
  const selected = users.find((u) => u.id === userId) ?? null;
  const personName = (u: TenantUser) => u.name ?? u.phone;
  const ready = selected !== null;

  async function submit() {
    if (!ready) return;
    setError(null);
    try {
      await reset.mutateAsync(userId);
      setConfirming(false);
      setDone(true);
    } catch (e) {
      setError(t(`commandPin.error.${classifyPinResetError(e)}`));
      setConfirming(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('commandPin.title', { tenant: tenant.name })}
      className="fixed inset-0 z-50 flex items-stretch justify-center overflow-y-auto bg-black/60 p-0 sm:items-start sm:p-8"
    >
      <div className="w-full min-h-dvh rounded-none border-0 bg-raad-panel p-5 pt-[calc(1.25rem+var(--raad-safe-top))] sm:min-h-0 sm:max-w-lg sm:rounded-2xl sm:border sm:border-raad-border sm:p-6 sm:pt-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-[17px] font-semibold">
            {t('commandPin.title', { tenant: tenant.name })}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="min-h-9 rounded-lg border border-raad-border px-3 text-[12.5px] font-semibold"
          >
            {t('commandPin.close')}
          </button>
        </div>

        {done && selected ? (
          <div>
            <p className="rounded-lg border border-raad-primary/40 bg-raad-primary/10 p-3 text-[12.5px]">
              {t('commandPin.done', { person: personName(selected), tenant: tenant.name })}
            </p>
            <p className="mt-2 rounded-lg border border-raad-warn/40 bg-raad-warn/10 p-3 text-[12.5px]">
              {t('commandPin.doneDetail')}
            </p>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-white"
              >
                {t('commandPin.doneButton')}
              </button>
            </div>
          </div>
        ) : confirming && selected ? (
          <div>
            <p className="rounded-lg border border-raad-warn/40 bg-raad-warn/10 p-3 text-[12.5px]">
              {t('commandPin.confirmBody', {
                person: personName(selected),
                tenant: tenant.name,
              })}
            </p>
            {error ? (
              <p
                role="alert"
                className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-[12.5px]"
              >
                {error}
              </p>
            ) : null}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                disabled={reset.isPending}
                className="min-h-10 rounded-lg border border-raad-border px-4 text-[13px] font-semibold disabled:opacity-40"
              >
                {t('commandPin.back')}
              </button>
              <button
                type="button"
                onClick={() => void submit()}
                disabled={reset.isPending}
                className="min-h-10 rounded-lg bg-raad-error px-4 text-[13px] font-semibold text-white disabled:opacity-40"
              >
                {reset.isPending ? t('commandPin.working') : t('commandPin.confirmButton')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="mb-4 rounded-lg border border-raad-border bg-raad-bg/40 p-3 text-[12px] text-raad-muted">
              {t('commandPin.intro', { tenant: tenant.name })}
            </p>
            <p className="mb-4 rounded-lg border border-raad-warn/40 bg-raad-warn/10 p-3 text-[12px]">
              {t('commandPin.explainer')}
            </p>

            {usersQ.isPending ? (
              <p className="text-sm text-raad-muted" role="status">
                {t('commandPin.loading')}
              </p>
            ) : usersQ.isError ? (
              <p role="alert" className="text-sm text-raad-error">
                {t('commandPin.usersError')}
              </p>
            ) : users.length === 0 ? (
              <p className="text-sm text-raad-muted">{t('commandPin.noUsers')}</p>
            ) : (
              <fieldset className="grid gap-2">
                <legend className="mb-1 text-[12px] font-medium text-raad-muted">
                  {t('commandPin.pick')}
                </legend>
                {users.map((u) => {
                  const disabled = u.disabledAt !== null;
                  return (
                    <label
                      key={u.id}
                      className={[
                        'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors',
                        userId === u.id
                          ? 'border-raad-primary/60 bg-raad-primary/10'
                          : 'border-raad-border hover:bg-raad-primary/5',
                      ].join(' ')}
                    >
                      <input
                        type="radio"
                        name="command-pin-user"
                        value={u.id}
                        checked={userId === u.id}
                        onChange={() => setUserId(u.id)}
                        className="mt-1"
                      />
                      <span className="block">
                        <span className="block text-[13.5px] font-medium">
                          {u.name ?? u.phone}
                          {disabled ? (
                            <span className="ms-2 inline-flex rounded-md border border-raad-muted/40 bg-raad-muted/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-raad-muted">
                              {t('commandPin.disabled')}
                            </span>
                          ) : null}
                        </span>
                        <span className="mt-0.5 block text-[11.5px] text-raad-muted">
                          <span className="num">{u.phone}</span>
                          {' · '}
                          {t(`commandPin.role.${u.role}`)}
                          {' · '}
                          {u.hasCommandPin ? t('commandPin.hasPin') : t('commandPin.noPin')}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </fieldset>
            )}

            {error ? (
              <p
                role="alert"
                className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-[12.5px]"
              >
                {error}
              </p>
            ) : null}

            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setConfirming(true);
                }}
                disabled={!ready}
                className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-white disabled:opacity-40"
              >
                {t('commandPin.review')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
